import { useEffect, useState } from 'react'
import { importStructureCsvIntoNode } from 'src/endpoints'

import { useApiRequest } from '@/endpoints/hooks'
import { CsvImportRowDto, StructureExportDto } from '@/endpoints/models'
import { useAppContext, useAppDispatch } from '@/hooks'
import { useCheckJsonMutation, useImportStructuresMutation } from '@/rtkApi'
import { setApiError } from '@/store/modules/apiError/actions'
import { loadSystemNodes } from '@/store/modules/node/actions'
import { loadNode } from '@/store/modules/node/general-actions'
import { getApiErrorDescription, logError } from '@/utils/errorHandling'

import {
	useSelectedBranchNumericId,
	useTreePanelOperations,
} from '../TreePanelContext'

export type ImportStructureProps = {
	caseSensitive: boolean,
	createNewDomains: boolean,
	generateCodes: boolean,
	importAllColumns: boolean,
	namesNormalisation: 'NONE' | 'UPPERCASE_FIRST_CHARS' | 'UPPERCASE_ALL'
}

export type JsonImportParams = {
	parentId?: number
	existingElementsMode?: 'OVERWRITE' | 'MERGE'
	createDomains?: boolean
	ignoreDomainDuplicities?: boolean
	ignoreInconsistentDomain?: boolean
	createStereotypes?: boolean
	ignoreMissingBo?: boolean
	ignoreMissingObjectTypes?: boolean
	ignoreTechnicalColumnCollision?: boolean
	fkMissingTable?: 'IGNORE' | 'IMPORT'
	fkMoreTables?: 'IGNORE' | 'IMPORT_EXITING' | 'IMPORT_IMPORTED'
	detailReport?: string
}

export const useImport = (onImported: () => void, nodeId?: number) => {
	const { t } = useAppContext()
	const dispatch = useAppDispatch()
	const { refreshSystemsSilent } = useTreePanelOperations()
	const branchId = useSelectedBranchNumericId()
	const [file, setFile] = useState(null as File | null)
	const [structure, setStructure] = useState(null as StructureExportDto | null)
	const [csvData, setCsvData] = useState(null as string | null)
	const [csvRows, setCsvRows] = useState<CsvImportRowDto[]>()
	const [importProps, setImportProps] = useState<ImportStructureProps>()
	const [jsonImportParams, setJsonImportParams] = useState<JsonImportParams | null>(null)
	const [loading, setLoading] = useState(false)
	const [_type, setType] = useState(null as string | null)
	const request = useApiRequest()
	const [checkJson, { isLoading: checkJsonLoading }] = useCheckJsonMutation()
	const [importJson] = useImportStructuresMutation()

	useEffect(() => {
		if (!file) {
			setStructure(null)
			setCsvData(null)
			setJsonImportParams(null)
			return
		}
	}, [file])

	useEffect(() => {
		if (!structure) setJsonImportParams(null)
	}, [structure])

	useEffect(() => {
		if (!file) return

		const reader = new FileReader()

		reader.onload = () => {
			if (file.type === 'application/json') {
				try {
					const structure = JSON.parse(
						reader.result as string,
					) as StructureExportDto

					setStructure(structure)
					setCsvData(null)
					setType('json')
				} catch {
					dispatch(
						setApiError({
							title: t('IMPORT_ERROR_TITLE'),
							description: t('IMPORT_ERROR_DESCRIPTION'),
							message: null,
						}),
					)

					setStructure(null)
					setCsvData(null)
					setFile(null)
				}
			} else if (
				file.type === 'application/vnd.ms-excel' ||
				file.type === 'text/csv'
			) {
				setCsvData(reader.result as string)
				setStructure(null)
				setType('csv')
			} else {
				dispatch(
					setApiError({
						title: t('IMPORT_ERROR_TITLE'),
						description: t('IMPORT_ERROR_DESCRIPTION'),
						message: null,
					}),
				)

				setStructure(null)
				setFile(null)
				setCsvData(null)
			}
		}

		reader.readAsText(file)
	}, [dispatch, file, t])

	const handleFile = (files: File[]) => {
		setFile(files[0])
	}

	const runJsonCheck = async (
		structurePayload: StructureExportDto,
		params: JsonImportParams,
	) => {
		const result = await checkJson({
			structureExportDto: structurePayload,
			parentId: nodeId,
			branchId,
			...params,
		}).unwrap()
		return result
	}

	const handleImport = async () => {
		if (structure) {
			if (!jsonImportParams) return
			setLoading(true)
			try {
				await importJson({
					structureExportDto: structure,
					parentId: nodeId,
					branchId,
					...jsonImportParams,
				}).unwrap()
			} catch (error) {
				logError(error, { operation: 'useImport.handleImport' })
				const message = getApiErrorDescription(error)
				dispatch(
					setApiError({
						title: t('IMPORT_FAILED'),
						description: message || t('IMPORT_FAILED_UKNOWN_ERROR'),
						message: null,
					}),
				)
				return
			} finally {
				setLoading(false)
			}

			// update nodes after import in tree panel
			if (nodeId) dispatch(loadNode(nodeId))
			else await dispatch(loadSystemNodes())
			refreshSystemsSilent().catch((e) =>
				logError(e, { operation: 'useImport.refreshSystemsSilent' }),
			)

			onImported()
		} else if (csvData) {
			setLoading(true)

			if (nodeId && csvRows && importProps) {
				await request(importStructureCsvIntoNode(nodeId, csvRows, importProps))
			}

			// update nodes after import in tree panel
			if (nodeId) dispatch(loadNode(nodeId))
			else await dispatch(loadSystemNodes())
			refreshSystemsSilent().catch((e) =>
				logError(e, { operation: 'useImport.refreshSystemsSilent' }),
			)

			onImported()

			setLoading(false)
		}
	}

	return {
		handleFile,
		handleImport,
		loading,
		structure,
		csvData,
		setCsvRows,
		setImportProps,
		file,
		setFile,
		runJsonCheck,
		jsonImportParams,
		setJsonImportParams,
		checkJsonLoading,
	}
}
